var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { BackSide, Mesh, MeshBasicMaterial, PlaneGeometry, Raycaster, TextureLoader } from "three";
import { ButtonSignal, ButtonType, ControllerButtonMessage, RemoveSubModelMessage } from "flavr-common";
import { Quaternion, Vector2, Vector3 } from "flavr-math";
import { ControllerConnectedMessage, ControllerDisconnectedMessage, ControllerMovedMessage, MouseConnectedMessage } from "@messages/controller/ControllerMessages";
import { EnableControllerSelectionMessage } from "@messages/selection/SelectionStrategyMessages";
import { inputService } from "@modules/inputs/input/InputService";
import { ModelEventHandler, ModelEventType, NewModule } from "@modules/module";
import { mainScene } from "@modules/rendering/mainScene";
import { MeshRenderable } from "@modules/rendering/mesh/MeshRenderable";
import { isRenderableType } from "@modules/rendering/renderable/Renderable";
import { getTextSelection, triggerClickEvent, triggerMouseEvent } from "./HTMLUtils";
import { htmlCanvasInteractiveObjectService } from "./HtmlCanvasInteractiveObjectService";
import { HTMLUserEvents } from "./HTMLUserEvents";
import { findPositionInGeomtryByUV } from "./GeometryUtils";
const tmpVector31 = new Vector3();
const tmpVector32 = new Vector3();
const tmpVector33 = new Vector3();
const tmpQuaternion = new Quaternion();
const tmpVector2 = new Vector2();
const FORWARD_VECTOR = new Vector3(0, 0, -1);
const PLANE_GEOMETRY = new PlaneGeometry(1, 1);
/**
 * Bridges 3D controller and mouse rays into HTML canvas interactions so embedded UI surfaces can be clicked, scrolled and edited from the world.
 *
 * Messages:
 * - `MouseConnectedMessage` and `ControllerConnectedMessage` - Create cursor/controller ray state for a newly connected mouse or controller.
 * - `ControllerMovedMessage` - Casts the controller's ray against interactive HTML canvas surfaces, updates the reticle/hover state and forwards scroll drags while a button is held.
 * - `ControllerButtonMessage` - Turns a select-button click into an HTML click (and, for input fields, starts caret/selection tracking) or updates hold/release drag state.
 * - `ControllerDisconnectedMessage` - Removes the controller's tracked state and clears any active input focus it held.
 * - `RemoveSubModelMessage` - Clears tracked input focus state for controllers whose focused HTML surface was removed.
 *
 * Events:
 * - `ModelEventType.TRANSFORM_UPDATED` - Repositions the text caret to follow a model whose HTML surface currently has focused input.
 */
export class HTMLCanvasCursorModule extends NewModule {
    /**
     * Loads the reticle texture and subscribes to mouse/controller connect, move, button and disconnect messages
     * needed to drive ray-based HTML canvas interaction.
     */
    constructor() {
        super("HTMLCanvasCursor");
        this.controllers = new Map();
        this.raycaster = new Raycaster();
        this.caretMaterial = new MeshBasicMaterial({ color: 0x000000, side: BackSide });
        this.models = new Map();
        this.offsetScaleHeight = 1.5;
        this.offsetLeft = 1;
        this.handleMouseConnectedMessage = (msg) => {
            this.controllers.set(msg.uid, {
                rayOrigin: new Vector3(),
                rayDirection: new Vector3(0, 0, -1),
                inputState: ButtonSignal.RELEASED,
                lastIntersectCoords: new Vector2(),
                inView: false,
                reticle: this.createReticleMesh(),
                caretMesh: this.createCaretMesh()
            });
        };
        this.handleControllerConnectedMessage = (msg) => {
            this.controllers.set(msg.uid, {
                rayOrigin: new Vector3().fromArray(msg.rayOrigin),
                rayDirection: new Vector3().fromArray(msg.rayDirection),
                inputState: ButtonSignal.RELEASED,
                lastIntersectCoords: new Vector2(),
                inView: false,
                reticle: this.createReticleMesh(),
                caretMesh: this.createCaretMesh()
            });
        };
        this.handleControllerMovedMessage = (msg) => {
            const controller = this.controllers.get(msg.uid);
            if (controller === undefined)
                return;
            tmpQuaternion.fromArray(msg.orientation);
            const rayOrigin = tmpVector31
                .copy(controller.rayOrigin)
                .applyQuaternion(tmpQuaternion)
                .multiplyScalar(msg.scale)
                .add(tmpVector33.fromArray(msg.position));
            const rayDir = tmpVector32.copy(controller.rayDirection).applyQuaternion(tmpQuaternion);
            this.raycaster.set(rayOrigin, rayDir);
            const intersectResult = this.intersectTest(controller);
            if (intersectResult === undefined) {
                if (controller.inView) {
                    controller.inView = false;
                    this.connection.send(new EnableControllerSelectionMessage(true, msg.uid));
                }
                return;
            }
            if (!controller.inView) {
                controller.inView = true;
                this.connection.send(new EnableControllerSelectionMessage(false, msg.uid));
            }
            for (const ele of intersectResult.selectedElements) {
                this.emitMouseMoveEvent(ele, intersectResult.windowCoords);
            }
            if (controller.inputState === ButtonSignal.CLICKED) {
                controller.inputState = ButtonSignal.RELEASED;
            }
            else if (controller.inputState === ButtonSignal.HOLD) {
                for (const element of intersectResult.selectedElements) {
                    const scrollable = this.getScrollableElement(intersectResult.rootElement, element);
                    if (scrollable === undefined)
                        continue;
                    const deltaX = controller.lastIntersectCoords.x - intersectResult.windowCoords.x;
                    const deltaY = controller.lastIntersectCoords.y - intersectResult.windowCoords.y;
                    scrollable.scrollLeft += deltaX;
                    scrollable.scrollTop += deltaY;
                }
            }
            controller.lastIntersectCoords.copy(intersectResult.windowCoords);
        };
        this.handleControllerDisconnectedMessage = (msg) => {
            const controller = this.controllers.get(msg.uid);
            if (controller === undefined)
                return;
            this.controllers.delete(msg.uid);
            this.deleteInputElementFromController(controller);
        };
        this.handleControllerButtonMessage = (msg) => {
            if (msg.buttonType !== ButtonType.SELECT)
                return;
            const controller = this.controllers.get(msg.uid);
            if (controller === undefined)
                return;
            if (msg.input === ButtonSignal.CLICKED) {
                this.deleteInputElementFromController(controller);
                controller.inputState = ButtonSignal.CLICKED;
                this.onControllerClick(controller);
            }
            else if (msg.input === ButtonSignal.HOLD) {
                controller.inputState = ButtonSignal.HOLD;
            }
            else if (msg.input === ButtonSignal.HOLD_RELEASED) {
                controller.inputState = ButtonSignal.RELEASED;
            }
        };
        this.handleRemoveSubModelMessage = (msg) => {
            const model = this.models.get(msg.uid);
            if (model === undefined)
                return;
            for (const controller of model.controllers) {
                this.deleteInputElementFromController(controller);
            }
        };
        /// Input event handlers ///
        this.onKeyUp = (e) => {
            if (e.dispatcher === "html-gl") {
                return;
            }
            for (const controller of this.controllers.values()) {
                if (!controller.input)
                    continue;
                const newSelection = HTMLUserEvents.input(controller.input.element, e.key, controller.input.selectionStart, controller.input.selectionEnd);
                if (newSelection.selectionStart !== controller.input.selectionStart || newSelection.selectionEnd !== controller.input.selectionEnd) {
                    controller.input.selectionStart = newSelection.selectionStart;
                    controller.input.selectionEnd = newSelection.selectionEnd;
                    const objData = htmlCanvasInteractiveObjectService.getObjectData(controller.input.object);
                    if (!objData)
                        continue;
                    const selection = getTextSelection(controller.input.element, {
                        selectionStart: controller.input.selectionStart,
                        selectionEnd: controller.input.selectionEnd
                    });
                    const rootRect = controller.input.rootElement.getBoundingClientRect();
                    this.placeCaretOnGeometry(controller, selection, rootRect, objData);
                }
            }
        };
        const map = new TextureLoader().load("img/ray_marker.png");
        this.reticleMaterial = new MeshBasicMaterial({
            color: 0xaaaaaa,
            map,
            side: BackSide,
            transparent: true
        });
        this.connection.subscribeByType(MouseConnectedMessage, this.handleMouseConnectedMessage);
        this.connection.subscribeByType(ControllerConnectedMessage, this.handleControllerConnectedMessage);
        this.connection.subscribeByType(ControllerMovedMessage, this.handleControllerMovedMessage);
        this.connection.subscribeByType(ControllerDisconnectedMessage, this.handleControllerDisconnectedMessage);
        this.connection.subscribeByType(ControllerButtonMessage, this.handleControllerButtonMessage, `${ButtonType.SELECT}`);
        this.connection.subscribeByType(RemoveSubModelMessage, this.handleRemoveSubModelMessage);
    }
    /**
     * Registers the keyboard listener that forwards text input into the currently focused HTML canvas input field.
     */
    onInit() {
        document.addEventListener("keyup", this.onKeyUp, false);
    }
    createReticleMesh() {
        const reticle = new Mesh(PLANE_GEOMETRY, this.reticleMaterial);
        reticle.matrixAutoUpdate = false;
        reticle.position.set(0, 0, -0.5);
        reticle.scale.set(0.05, 0.05, 0.05);
        reticle.updateMatrix();
        reticle.renderOrder = 100;
        reticle.visible = false;
        mainScene.add(reticle);
        return reticle;
    }
    /// Handler methods ///
    handleModelTransformEvent(model, _localTransform) {
        const controllerInfo = this.models.get(model.getUID());
        if (controllerInfo === undefined)
            return;
        for (const controller of controllerInfo.controllers) {
            if (controller.input === undefined)
                continue;
            this.updateCaretTransform(controller, model.getWorldTransform(), model.getWorldOrientation(), model.getWorldScale());
        }
    }
    onControllerClick(controller) {
        const intersectResult = this.intersectTest(controller);
        if (intersectResult === undefined)
            return;
        for (const ele of intersectResult.selectedElements) {
            triggerClickEvent(ele, intersectResult.windowCoords.x, intersectResult.windowCoords.y);
            if (ele instanceof HTMLInputElement) {
                this.onInputElementClick(controller, intersectResult, ele);
            }
        }
    }
    onInputElementClick(controller, intersectResult, input) {
        const selection = getTextSelection(input, {
            windowCoords: intersectResult.windowCoords
        });
        const caretScale = !!selection.rect.height && !!intersectResult.rootRect.height ? selection.rect.height / intersectResult.rootRect.height : 0.05;
        const modelUid = intersectResult.objData.renderable ? intersectResult.objData.renderable.owner.modelUID : undefined;
        controller.input = {
            rootElement: intersectResult.rootElement,
            element: input,
            selectionStart: selection.start,
            selectionEnd: selection.end,
            localPosition: new Vector3(),
            normal: new Vector3(),
            caretScale,
            object: intersectResult.object,
            inputDisabledId: inputService.setDisabled(),
            modelUid: intersectResult.objData.renderable ? intersectResult.objData.renderable.owner.modelUID : undefined
        };
        if (this.placeCaretOnGeometry(controller, selection, intersectResult.rootRect, intersectResult.objData)) {
            controller.caretMesh.visible = true;
        }
        if (modelUid !== undefined) {
            let model = this.models.get(modelUid);
            if (!model) {
                model = {
                    controllers: new Set()
                };
                this.models.set(modelUid, model);
            }
            model.controllers.add(controller);
        }
    }
    emitMouseMoveEvent(element, point) {
        triggerMouseEvent(element, "mousemove", { x: point.x, y: point.y });
    }
    /// Modify caret ///
    createCaretMesh() {
        const mesh = new Mesh(PLANE_GEOMETRY, this.caretMaterial);
        mesh.matrixAutoUpdate = false;
        mesh.position.set(0, 0, -0.5);
        mesh.scale.set(0.005, 0.1, 0.1);
        mesh.updateMatrix();
        mesh.renderOrder = 100;
        mesh.visible = false;
        mainScene.add(mesh);
        return mesh;
    }
    placeCaretOnGeometry(controller, selection, rootRect, objData) {
        if (!controller.input)
            return false;
        const inputData = controller.input;
        if (!objData.renderable)
            return;
        const owner = objData.renderable.owner;
        if (!owner.renderable)
            return false;
        if (!isRenderableType(MeshRenderable, owner.renderable))
            return false;
        const cursorPosInUVSpace = tmpVector2.set((selection.rect.x - rootRect.left - this.offsetLeft) / rootRect.width, (selection.rect.y + (selection.rect.height / 2) * (this.offsetScaleHeight / 2) - rootRect.top) / rootRect.height);
        controller.caretMesh.scale.y = inputData.caretScale * owner.worldScale.y * this.offsetScaleHeight;
        const geometryData = owner.renderable.getGeometry();
        if (!geometryData)
            return false;
        findPositionInGeomtryByUV(geometryData.geometry, cursorPosInUVSpace, inputData.localPosition, inputData.normal);
        this.updateCaretTransform(controller, owner.matrixWorld, objData.renderable.owner.worldOrientation, owner.worldScale);
        return true;
    }
    updateCaretTransform(controller, matrixWorld, worldOrientation, worldScale) {
        if (!controller.input)
            return;
        controller.caretMesh.quaternion.setFromUnitVectors(FORWARD_VECTOR, controller.input.normal);
        controller.caretMesh.quaternion.multiplyQuaternions(worldOrientation, controller.caretMesh.quaternion);
        controller.caretMesh.scale.y = controller.input.caretScale * worldScale.y * this.offsetScaleHeight;
        controller.caretMesh.position.copy(controller.input.normal).multiplyScalar(0.02).add(controller.input.localPosition).applyMatrix4(matrixWorld);
        controller.caretMesh.updateMatrix();
    }
    intersectTest(controller) {
        controller.reticle.visible = false;
        const intersects = this.raycaster.intersectObjects(htmlCanvasInteractiveObjectService.interactiveObjects);
        if (intersects.length === 0)
            return undefined;
        const intersect = intersects[0];
        if (intersect.face === undefined || intersect.face === null)
            return undefined;
        const uv = intersect.uv;
        if (!uv)
            return undefined;
        const objData = htmlCanvasInteractiveObjectService.getObjectData(intersect.object);
        if (objData === undefined)
            return undefined;
        const rootElement = objData.element;
        // flipY
        // uv.y = 1 - uv.y
        const rect = rootElement.getBoundingClientRect();
        const windowCoords = tmpVector2.set(uv.x * rect.width + rect.left, uv.y * rect.height + rect.top);
        const selectedElements = this.getElementByCoordinates(rootElement, windowCoords);
        if (selectedElements === undefined)
            return undefined;
        tmpVector31.copy(intersect.face.normal);
        controller.reticle.quaternion.setFromUnitVectors(FORWARD_VECTOR, intersect.face.normal);
        if (objData.renderable) {
            controller.reticle.quaternion.multiplyQuaternions(objData.renderable.owner.worldOrientation, controller.reticle.quaternion);
            tmpVector31.applyQuaternion(objData.renderable.owner.worldOrientation);
        }
        controller.reticle.position.copy(tmpVector31).multiplyScalar(0.02).add(intersect.point);
        controller.reticle.updateMatrix();
        controller.reticle.visible = true;
        return {
            selectedElements,
            windowCoords,
            uv: new Vector2().copy(uv),
            hitNormal: new Vector3().copy(intersect.face.normal),
            point: new Vector3().copy(intersect.point),
            rootElement,
            rootRect: rect,
            object: intersect.object,
            objData
        };
    }
    /// Private element getters ///
    getElementByCoordinates(rootElement, point) {
        const elements = [];
        // elementsFromPoint only works on visible elements
        // const elements = document.elementsFromPoint(point.x, point.y);
        const traverse = (element) => {
            if (element.nodeType === Node.TEXT_NODE)
                return;
            if (element.nodeType === Node.COMMENT_NODE)
                return;
            const rect = element.getBoundingClientRect();
            if (point.x > rect.left && point.x < rect.right && point.y > rect.top && point.y < rect.bottom) {
                elements.push(element);
            }
            for (let i = 0; i < element.childNodes.length; i++) {
                traverse(element.childNodes[i]);
            }
        };
        traverse(rootElement);
        if (elements.length) {
            return elements;
        }
        return undefined;
    }
    getScrollableElement(rootElement, element) {
        while (element !== undefined && element !== rootElement) {
            if (element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth) {
                return element;
            }
            element = element.parentElement;
        }
        return undefined;
    }
    /// Private controller helper methods ///
    deleteInputElementFromController(controller) {
        controller.caretMesh.visible = false;
        if (!controller.input)
            return;
        this.deleteControllerFromCaretMap(controller);
        if (controller.input.inputDisabledId !== undefined) {
            inputService.setEnabled(controller.input.inputDisabledId);
        }
        controller.input = undefined;
    }
    deleteControllerFromCaretMap(controller) {
        if (!controller.input)
            return;
        if (controller.input.modelUid === undefined)
            return;
        const model = this.models.get(controller.input.modelUid);
        if (!model)
            return;
        model.controllers.delete(controller);
        if (model.controllers.size === 0) {
            this.models.delete(controller.input.modelUid);
        }
    }
}
__decorate([
    ModelEventHandler(ModelEventType.TRANSFORM_UPDATED)
], HTMLCanvasCursorModule.prototype, "handleModelTransformEvent", null);
